import { createAction, props } from '@ngrx/store';
import { ResourceData } from '../elements/resource/resource.types';
import { Library, Project } from './project.interface';
import { ProjectError } from './project.reducer';
import { ProjectFeature } from './project.feature';

function getActionName(action: string) {
  return `[${ProjectFeature}]_${action}`;
}
export const loadProjects = createAction(getActionName('list'));

export const loadProjectById = createAction(
  getActionName('get-one'),
  props<{ id: string }>(),
);
export const updateFileName = createAction(
  getActionName('update-file-name'),
  props<{ IRI: string; newName: string }>(),
);

export const updateFileNameBase = createAction(
  getActionName('update-file-name-base'),
  props<{ IRI: string; newName: string }>(),
);
export const setLibraryValue = createAction(
  getActionName('set-library-value'),
  props<{ key: keyof Library; varName: string; value: any }>(),
);

export const setRelationShips = createAction(
  getActionName('set-relationships'),
  props<{
    IRI: string;
    relationshipKey: string;
    relationships: Record<string, boolean>;
  }>(),
);

export const setRecentProjects = createAction(
  getActionName('set-recent-projects'),
  props<{ projects: Project[] }>(),
);

export const setExternalProjects = createAction(
  getActionName('set-external-projects'),
  props<{ projects: Project[] }>(),
);

export const deleteProject = createAction(
  getActionName('delete-project'),
  props<{ id: string }>(),
);

export const deleteFile = createAction(
  getActionName('delete-file'),
  props<{ IRI: string }>(),
);

// export const deleteScene = createAction(
//   getActionName('delete-scene'),
//   props<{ IRI: string }>(),
// );

export const createScene = createAction(
  getActionName('create-scene'),
  props<{ IRI: string; index: number; parentFileID: string }>(),
);

export const requestSuccess = createAction(getActionName('request-success'));

export const setFiles = createAction(
  getActionName('set-files'),
  props<{ files: Record<string, ResourceData> }>(),
);

export const saveProjectLanguages = createAction(
  getActionName('save-languages'),
  props<{ languages: string[] }>(),
);

export const saveProjectName = createAction(
  getActionName('save-name'),
  props<{ id?: string; name: string }>(),
);

export const saveProjectLanguagesBase = createAction(
  getActionName('save-languages-base'),
  props<{ languages: string[] }>(),
);

export const addColorPaletteItem = createAction(
  getActionName('save-languages-base'),
  props<{ variableName: string; color: string }>(),
);

export const setCurrentLanguage = createAction(
  getActionName('save-current-language'),
  props<{ language: string }>(),
);

export const stepLanguage = createAction(getActionName('step-language'));

export const saveProjectColorPalette = createAction(
  getActionName('save-color-palette'),
  props<{ colorPalette: Record<string, string> }>(),
);

export const saveProjectColorPaletteBase = createAction(
  getActionName('save-color-palette-base'),
  props<{ colorPalette: Record<string, string> }>(),
);

export const deleteProjectRequest = createAction(
  getActionName('delete-project-request'),
  props<{ id: string }>(),
);

export const deleteProjectRequestSuccess = createAction(
  getActionName('delete-project-request-success'),
);

export const deleteProjectRequestFailure = createAction(
  getActionName('delete-project-request-failure'),
  props<{ error: string }>(),
);

export const setDeleteIsPending = createAction(
  getActionName('set-delete-is-pending'),
  props<{ id: string }>(),
);

export const resetDeleteIsPending = createAction(
  getActionName('reset-delete-is-pending'),
);

export const loadProjectWithFilesById = createAction(
  getActionName('get-one-with-files'),
  props<{ id: string }>(),
);

export const patchProjectDescriptorSuccess = createAction(
  getActionName('patch-project-descriptor-success'),
  props<{ project: ResourceData }>(),
);

export const setProjectError = createAction(
  getActionName('set-error'),
  props<{ error: ProjectError }>(),
);

export const createProject = createAction(
  getActionName('create'),
  props<{ name: string }>(),
);

export const setNewlyCreatedProject = createAction(
  getActionName('set-newly-created-project'),
  props<{ id: string; time: number }>(),
);

export const setProject = createAction(
  getActionName('set'),
  props<{ project: Project }>(),
);

export const setProjectLoaded = createAction(
  getActionName('set-project-loaded'),
  props<{ value: boolean }>(),
);

export const deleteComponent = createAction(
  'editor:delete-component',
  props<{
    IRI?: string;
  }>(),
);

export const addShapeToFile = createAction(
  getActionName('add-shape-to-file'),
  props<{ shapeIRI: string; fileIRI: string }>(),
);
export const setCurrentFileID = createAction(
  getActionName('set-current-file-id'),
  props<{ id: string }>(),
);

export const setCurrentFileIRI = createAction(
  getActionName('set-current-file-iri'),
  props<{ IRI: string }>(),
);

export const setCurrentSceneID = createAction(
  getActionName('set-current-scene-id'),
  props<{ id: string }>(),
);

export const setNewProjectLoading = createAction(
  getActionName('set-new-project-loading'),
  props<{ value: boolean }>(),
);

export const deleteProjectFromStore = createAction(
  getActionName('delete-project-from-store'),
  props<{ id: string }>(),
);

export const setProjects = createAction(
  getActionName('set list'),
  props<{ projects: Project[] }>(),
);

export const setCurrentProject = createAction(
  getActionName('set-current'),
  props<{ id: string }>(),
);

export const saveProjectDescriptor = createAction(
  getActionName('save-project-descriptor'),
);

export const setFile = createAction(
  getActionName('set-file'),
  props<{
    file: ResourceData;
  }>(),
);

export const addFileToProject = createAction(
  getActionName('add-file-to-project'),
  props<{
    label: string;
  }>(),
);

export const addFileToProjectRequest = createAction(
  getActionName('add-file-to-project-request'),
  props<{
    projectID: string;
    label: string;
  }>(),
);
export const setNewFileLoading = createAction(
  getActionName('set-new-file-loading'),
  props<{
    value: boolean;
  }>(),
);

export const addSceneAfter = createAction(
  'editor:add-scene-after',
  props<{ IRI: string; parentFileID?: string; label?: string }>(),
);

export const addSceneRequest = createAction(
  'editor:add-scene-request',
  props<{
    IRI: string;
    parentFileID?: string;
    sceneIndex: number;
    label?: string;
  }>(),
);
export const addSubSceneAfter = createAction(
  'editor:add-scene-after',
  props<{ sceneID?: string }>(),
);

export const descriptorMapTest = createAction('editor:descriptor-map-test');
